import React, { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Buffer } from "buffer";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { InputField } from "src/shared_components/FormElements";
import ChipInputField from "src/shared_components/FormElements/ChipInputField";
import EmailEditor from "./EmailEditor";
import CustomLoader from "src/shared_components/CustomLoader";
import { useSnackbar } from "src/context/ShowMessage";
import { useTranslation } from "react-i18next";
import { sendEmail } from "src/apis/ContractApis";
import { getAllClienttemplates } from "src/apis/ClientAPI";

interface Doc {
  clientId: number;
  fieldId: number;
  objectTypeId: number;
  fileId: number;
  directory: string;
  fileName: string;
  isDefault: boolean;
  viewName: string;
  contentType: string;
  fileByte: string;
  url: string | null;
  isImage: boolean;
  file: any | null;
  type: string;
  key?: string;
  variableKey?: string;
}

interface DocType {
  label: string;
  type: string;
  defaultSelected?: boolean;
  fetch?: (id: number) => Promise<any>;
  previewFetch?: (doc: Doc) => Promise<any>;
  fileFetch?: (doc: Doc) => Promise<any>;
  locationId?: number;
  templateReferenceId?: string;
}

interface TemplateItem {
  templateId?: number;
  templateName?: string;
  filePath?: string;
  path?: string;
  name?: string;
}

interface EmailDialogProps {
  open: boolean;
  onClose: any;
  fetchData?: any;
  sendFunction?: any;
  title: string;
  id?: number;
  docTypes?: any;
}

const EmailDialog: React.FC<EmailDialogProps> = ({
  open,
  onClose,
  fetchData,
  title,
  sendFunction,
  id = 0,
  docTypes,
}) => {
  const { t } = useTranslation();

  const schema = yup.object().shape({
    fromName: yup.string().required(t("email.validation.fromNameRequired", "From Name is required")),
    subject: yup.string().required(t("email.validation.subjectRequired", "Subject is required")),
    to: yup
      .array()
      .of(yup.string().email(t("email.validation.invalidEmail", "Invalid email format")))
      .min(1, t("email.validation.recipientRequired", "At least one recipient is required"))
      .required(t("email.validation.toRequired", "To field is required")),
    cc: yup.array().of(yup.string().email(t("email.validation.invalidEmail", "Invalid email format"))).nullable(),
  });

  const { displayMessage } = useSnackbar();
  const [template, setTemplate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [docLoading, setDocLoading] = useState<boolean>(false);
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [availableDocs, setAvailableDocs] = useState<Doc[]>([]);
  const [selectedInsuranceDocs, setSelectedInsuranceDocs] = useState<string[]>(
    [],
  );
  const [draftSelectedInsuranceDocs, setDraftSelectedInsuranceDocs] = useState<
    string[]
  >([]);
  const [vehicleInsuranceDocumentsOpen, setVehicleInsuranceDocumentsOpen] =
    useState<boolean>(false);
  const [selectedOtherDocs, setSelectedOtherDocs] = useState<string[]>([]);
  const [draftSelectedOtherDocs, setDraftSelectedOtherDocs] = useState<
    string[]
  >([]);
  const [otherDocumentsOpen, setOtherDocumentsOpen] = useState<boolean>(false);
  const [otherPreviewDoc, setOtherPreviewDoc] = useState<Doc | null>(null);
  const [otherPreviewUrl, setOtherPreviewUrl] = useState<string | null>(null);
  const [otherPreviewOpen, setOtherPreviewOpen] = useState(false);
  const [selectedTemplatePaths, setSelectedTemplatePaths] = useState<
    Record<string, string | null>
  >({});
  const [templateOptions, setTemplateOptions] = useState<TemplateItem[]>([]);
  const [templateType, setTemplateType] = useState<string | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const fetchDataRef = useRef(fetchData);
  const loadedTemplateKeyRef = useRef<string | null>(null);
  const defaultSelectedDocsKeyRef = useRef<string | null>(null);

  const otherDocType = docTypes?.find((doc: DocType) => doc.label === "Other");
  const otherAttachmentType = otherDocType?.type;
  const isOtherType = (type: string) => type === otherAttachmentType;

  const addSelectedTemplateDocument = (
    type: string,
    path: string | null,
    name?: string,
  ) => {
    const docType = docTypes?.find((doc: DocType) => doc.type === type);
    setAvailableDocs((prev) => [
      ...prev.filter((doc) => doc.type !== type),
      {
        clientId: 0,
        fieldId: id,
        objectTypeId: 0,
        fileId: 0,
        directory: path || "",
        fileName: name || docType?.label || type,
        isDefault: false,
        viewName: docType?.label || type,
        contentType: "application/pdf",
        fileByte: "",
        url: null,
        isImage: false,
        file: null,
        type,
      },
    ]);
  };

  const methods = useForm<any>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      fromName: "",
      subject: "",
      to: [],
      cc: [],
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    if (!open) {
      loadedTemplateKeyRef.current = null;
      defaultSelectedDocsKeyRef.current = null;
      return;
    }

    if (!fetchDataRef.current) return;

    const templateKey = `${id}:${title}`;
    if (loadedTemplateKeyRef.current === templateKey) return;

    loadedTemplateKeyRef.current = templateKey;

    const fetchEmailTemplate = async () => {
      setLoading(true);
      try {
        const response = await fetchDataRef.current?.();
        if (response?.status === 200 && response?.data) {
          reset(response.data);
          setTemplate(response.data.emailBody);
        } else {
          loadedTemplateKeyRef.current = null;
          displayMessage(
            response?.data || "Failed to fetch email content",
            "error",
          );
        }
      } catch (error: any) {
        loadedTemplateKeyRef.current = null;
        displayMessage(error.message, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchEmailTemplate();
  }, [open, id, title, reset, displayMessage]);

  const openOtherDocumentsSelector = async () => {
    setDraftSelectedOtherDocs(selectedOtherDocs);
    setOtherDocumentsOpen(true);

    const docFetch = otherDocType?.fetch;
    if (!docFetch) return;

    setDocLoading(true);
    try {
      const response = await docFetch(id);
      if (response?.status === 200 || response?.status === 404) {
        const documents = Array.isArray(response?.data) ? response.data : [];
        const newDocs = documents.map((doc: Doc) => ({
          ...doc,
          type: otherAttachmentType,
        }));
        setAvailableDocs((prev) => [
          ...prev.filter((doc) => !isOtherType(doc.type)),
          ...newDocs,
        ]);
      } else {
        displayMessage(
          response?.data || "Failed to fetch Other documents",
          "error",
        );
      }
    } catch (error: any) {
      displayMessage(error.message, "error");
    } finally {
      setDocLoading(false);
    }
  };

  const handleDocCheckboxChange = async (type: string, checked: boolean) => {
    if (isOtherType(type)) {
      if (checked) {
        await openOtherDocumentsSelector();
      } else {
        setSelectedDocTypes((prev) => prev.filter((doc) => !isOtherType(doc)));
        setSelectedOtherDocs([]);
        setDraftSelectedOtherDocs([]);
        setOtherDocumentsOpen(false);
      }
      return;
    }

    const selectedDocType = docTypes?.find(
      (doc: DocType) => doc.type === type,
    );
    if (selectedDocType?.templateReferenceId) {
      if (!checked) {
        setSelectedDocTypes((prev) => prev.filter((doc) => doc !== type));
        setAvailableDocs((prev) => prev.filter((doc) => doc.type !== type));
        setSelectedTemplatePaths((prev) => {
          const updated = { ...prev };
          delete updated[type];
          return updated;
        });
        return;
      }

      setTemplateLoading(true);
      try {
        const response = await getAllClienttemplates(
          selectedDocType.locationId,
          selectedDocType.templateReferenceId,
          { isEmailable: true },
        );
        const templates = Array.isArray(response?.data) ? response.data : [];

        if (response?.status !== 200) {
          displayMessage(
            response?.data || "Failed to fetch PDF templates",
            "error",
          );
          return;
        }

        if (templates.length === 0) {
          setSelectedTemplatePaths((prev) => ({ ...prev, [type]: null }));
          addSelectedTemplateDocument(type, null, selectedDocType.label);
          setSelectedDocTypes((prev) =>
            prev.includes(type) ? prev : [...prev, type],
          );
        } else if (templates.length === 1) {
          const path = templates[0]?.filePath || templates[0]?.path || null;
          setSelectedTemplatePaths((prev) => ({ ...prev, [type]: path }));
          addSelectedTemplateDocument(
            type,
            path,
            templates[0]?.templateName || templates[0]?.name,
          );
          setSelectedDocTypes((prev) =>
            prev.includes(type) ? prev : [...prev, type],
          );
        } else {
          setTemplateOptions(templates);
          setTemplateType(type);
          setTemplateDialogOpen(true);
        }
      } catch (error: any) {
        displayMessage(error.message || "Failed to fetch PDF templates", "error");
      } finally {
        setTemplateLoading(false);
      }
      return;
    }

    setSelectedDocTypes((prev) =>
      checked ? [...prev, type] : prev.filter((d) => d !== type),
    );

    if (checked) {
      const docFetch = docTypes?.find((d: any): any => d.type === type)?.fetch;
      if (docFetch) {
        setDocLoading(true);
        try {
          const res = await docFetch(id);
          if (res?.status === 200 && res?.data) {
            const newDocs = res.data.map((doc: Doc) => ({
              ...doc,
              type,
            }));
            setAvailableDocs((prev) => [
              ...prev.filter((d) => d.type !== type),
              ...newDocs,
            ]);
            if (type === "VehicleInsurance") {
              const insuranceDocIds = newDocs
                .map((doc: Doc) => doc.directory);
              setDraftSelectedInsuranceDocs(
                selectedInsuranceDocs.filter((docId) =>
                  insuranceDocIds.includes(docId),
                ),
              );
              setVehicleInsuranceDocumentsOpen(true);
            }
          } else {
            displayMessage(
              res?.data || `Failed to fetch ${type} documents`,
              "error",
            );
          }
        } catch (error: any) {
          displayMessage(error.message, "error");
        } finally {
          setDocLoading(false);
        }
      } else {
        setAvailableDocs((prev) => [
          ...prev.filter((d) => d.type !== type),
          {
            clientId: 0,
            fieldId: id,
            objectTypeId: 0,
            fileId: 0,
            directory: type,
            fileName: type,
            isDefault: false,
            viewName: type,
            contentType: "",
            fileByte: "",
            url: null,
            isImage: false,
            file: null,
            type,
          },
        ]);
      }
    } else {
      setAvailableDocs((prev) => prev.filter((d) => d.type !== type));
      if (type === "VehicleInsurance") {
        setSelectedInsuranceDocs([]);
        setDraftSelectedInsuranceDocs([]);
        setVehicleInsuranceDocumentsOpen(false);
      }
    }
  };

  useEffect(() => {
    if (!open || !docTypes?.length) return;

    const defaultDocTypes = docTypes
      .filter((doc: DocType) => doc.defaultSelected)
      .map((doc: DocType) => doc.type);

    if (!defaultDocTypes.length) return;

    const defaultSelectionKey = `${id}:${defaultDocTypes.join("|")}`;
    if (defaultSelectedDocsKeyRef.current === defaultSelectionKey) return;

    defaultSelectedDocsKeyRef.current = defaultSelectionKey;

    defaultDocTypes.forEach((type: string) => {
      if (!selectedDocTypes.includes(type)) {
        handleDocCheckboxChange(type, true);
      }
    });
  }, [docTypes, id, open, selectedDocTypes]);

  const handleInsuranceDocChange = (docId: string, checked: boolean) => {
    const updateDocs = (prev: string[]) =>
      checked ? [...prev, docId] : prev.filter((id: any) => id !== docId);

    if (vehicleInsuranceDocumentsOpen) {
      setDraftSelectedInsuranceDocs(updateDocs);
      return;
    }

    setSelectedInsuranceDocs(updateDocs);
  };

  const cancelVehicleInsuranceDocumentSelection = () => {
    setDraftSelectedInsuranceDocs(selectedInsuranceDocs);
    setVehicleInsuranceDocumentsOpen(false);

    if (selectedInsuranceDocs.length === 0) {
      setSelectedDocTypes((prev) =>
        prev.filter((type) => type !== "VehicleInsurance"),
      );
      setAvailableDocs((prev) =>
        prev.filter((doc) => doc.type !== "VehicleInsurance"),
      );
    }
  };

  const confirmVehicleInsuranceDocumentSelection = () => {
    setSelectedInsuranceDocs(draftSelectedInsuranceDocs);
    setSelectedDocTypes((prev) =>
      draftSelectedInsuranceDocs.length > 0 && !prev.includes("VehicleInsurance")
        ? [...prev, "VehicleInsurance"]
        : draftSelectedInsuranceDocs.length > 0
          ? prev
          : prev.filter((type) => type !== "VehicleInsurance"),
    );
    setVehicleInsuranceDocumentsOpen(false);
  };

  const getDocumentKey = (doc: Doc) =>
    doc.variableKey || doc.key || doc.directory;

  const handleOtherDocChange = (documentKey: string, checked: boolean) => {
    setDraftSelectedOtherDocs((prev) =>
      checked
        ? [...prev, documentKey]
        : prev.filter((key) => key !== documentKey),
    );
  };

  const cancelOtherDocumentSelection = () => {
    setDraftSelectedOtherDocs(selectedOtherDocs);
    setOtherDocumentsOpen(false);
  };

  const confirmOtherDocumentSelection = () => {
    setSelectedOtherDocs(draftSelectedOtherDocs);
    setSelectedDocTypes((prev) =>
      otherAttachmentType && !prev.includes(otherAttachmentType)
        ? [...prev, otherAttachmentType]
        : prev,
    );
    setOtherDocumentsOpen(false);
  };

  const removeSelectedOtherDocument = (documentKey: string) => {
    setSelectedOtherDocs((prev) => {
      const updated = prev.filter((key) => key !== documentKey);
      if (updated.length === 0) {
        setSelectedDocTypes((types) =>
          types.filter((type) => !isOtherType(type)),
        );
      }
      return updated;
    });
  };

  const getDocumentContentType = (doc: Doc) => {
    const contentType = (doc.contentType || "").toLowerCase();
    const fileName = (doc.fileName || "").toLowerCase();

    if (contentType === "application/pdf" || fileName.endsWith(".pdf")) {
      return "application/pdf";
    }
    if (contentType.startsWith("image/")) return contentType;
    if (/\.(png|jpe?g|gif|bmp|webp|svg)$/.test(fileName)) {
      const extension = fileName.split(".").pop();
      return extension === "jpg" ? "image/jpeg" : `image/${extension || "png"}`;
    }
    if (contentType.startsWith("text/") || fileName.endsWith(".txt")) {
      return contentType.startsWith("text/") ? contentType : "text/plain";
    }
    return contentType || "application/octet-stream";
  };

  const canPreviewOtherDocument = (doc: Doc) => {
    const contentType = getDocumentContentType(doc);
    return (
      contentType === "application/pdf" ||
      contentType.startsWith("image/") ||
      contentType.startsWith("text/")
    );
  };

  const handleOtherPreview = async (doc: Doc) => {
    if (!otherDocType?.fileFetch) return;

    try {
      const response = await otherDocType.fileFetch(doc);
      if (response?.status !== 200 || !response?.data) {
        throw new Error("Document content was not returned");
      }

      const binaryBuffer: any = Buffer.from(response.data, "base64");
      const blob = new Blob([binaryBuffer], {
        type: getDocumentContentType(doc),
      });
      if (otherPreviewUrl) URL.revokeObjectURL(otherPreviewUrl);
      setOtherPreviewDoc(doc);
      setOtherPreviewUrl(URL.createObjectURL(blob));
      setOtherPreviewOpen(true);
    } catch (error: any) {
      displayMessage(error.message || "Preview failed", "error");
    }
  };

  const closeOtherPreview = () => {
    setOtherPreviewOpen(false);
    if (otherPreviewUrl) URL.revokeObjectURL(otherPreviewUrl);
    setOtherPreviewDoc(null);
    setOtherPreviewUrl(null);
  };

  const selectPdfTemplate = (template: TemplateItem) => {
    if (!templateType) return;
    const path = template.filePath || template.path || null;
    setSelectedTemplatePaths((prev) => ({
      ...prev,
      [templateType]: path,
    }));
    addSelectedTemplateDocument(
      templateType,
      path,
      template.templateName || template.name,
    );
    setSelectedDocTypes((prev) =>
      prev.includes(templateType) ? prev : [...prev, templateType],
    );
    setTemplateDialogOpen(false);
    setTemplateOptions([]);
    setTemplateType(null);
  };

  const closeTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setTemplateOptions([]);
    setTemplateType(null);
  };

  const handlePreview = async (doc: Doc) => {
    const docType = docTypes?.find((d: any) => d.type === doc.type);
    if (docType?.previewFetch) {
      try {
        await docType.previewFetch(doc);
      } catch (error: any) {
        displayMessage(error.message, "error");
      }
    }
  };

  const onSubmit = async (values: any) => {
    setLoading(true);
    const details = [
      ...selectedDocTypes
        .filter((type) => type !== "VehicleInsurance" && !isOtherType(type))
        .map((type) => {
          const path = selectedTemplatePaths[type] ?? null;
          return {
            attachmentType: type,
            key: path,
            path,
          };
        }),

      ...selectedInsuranceDocs.map((docId) => {
        const selectedDoc = availableDocs.find(
          (doc) =>
            doc.type === "VehicleInsurance" && doc.directory === docId,
        );

        return {
          attachmentType: "VehicleInsurance",
          key: docId,
          path: docId,
          fileName: selectedDoc?.fileName,
        };
      }),

      ...selectedOtherDocs.map((documentKey) => ({
        attachmentType: otherAttachmentType,
        key: documentKey,
        path: documentKey,
      })),
    ];

    try {
      const body = {
        ...values,
        emailBody: template,
        fieldId: id,
        attachmentsDetails: details,
      };
      const response = sendFunction
        ? await sendFunction(id, body)
        : await sendEmail(id, body);

      if (response?.status === 200) {
        displayMessage("Email sent successfully", "success");
        onClose();
      } else {
        displayMessage(response?.data || "Failed to send email", "error");
      }
    } catch (error: any) {
      displayMessage(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const emailSchema = yup
    .string()
    .email("Invalid email format")
    .required("Email is required");

  const selectedAttachmentCount =
    selectedDocTypes.filter(
      (type) => type !== "VehicleInsurance" && !isOtherType(type),
    ).length +
    selectedInsuranceDocs.length +
    selectedOtherDocs.length;

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ style: { width: "80%", borderRadius: 8 } }}
      >
      <DialogTitle sx={{ fontWeight: "bold", bgcolor: "#f5f5f5", py: 2 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <CustomLoader />
        ) : (
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <InputField
                fieldName="fromName"
                control={control}
                label={t("common.labels.from")}
                error={!!errors?.fromName}
                helperText={errors?.fromName?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputField
                fieldName="subject"
                control={control}
                label={t("common.labels.subject")}
                error={!!errors?.subject}
                helperText={errors?.subject?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInputField
                fieldName="to"
                control={control}
                label={t("common.labels.to")}
                error={!!errors?.to}
                helperText={errors?.to?.message}
                setValue={setValue}
                schema={emailSchema}
                setError={setError}
                clearError={clearErrors}
                placeholder={t("common.placeholders.enterTo")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInputField
                fieldName="cc"
                control={control}
                label={t("common.labels.cc")}
                error={!!errors?.cc}
                helperText={errors?.cc?.message}
                setValue={setValue}
                schema={emailSchema}
                setError={setError}
                clearError={clearErrors}
                placeholder={t("common.placeholders.enterCc")}
              />
            </Grid>

            <Grid item xs={12}>
              <EmailEditor template={template} setTemplate={setTemplate} />
            </Grid>
            {docTypes?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t("common.labels.selectDocsAttach", "Select Documents to Attach")}
                </Typography>
                <Grid container spacing={2}>
                  {docTypes?.map((doc: any) => (
                    <Grid item key={doc.type}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedDocTypes.includes(doc.type)}
                            onChange={(e) =>
                              handleDocCheckboxChange(
                                doc.type,
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        }
                        label={t(`email.docType${doc.label.replace(/\s+/g, "")}`, doc.label) as string}
                        sx={{ mr: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                {docLoading && (
                  <CircularProgress size={20} sx={{ mt: 1, ml: 2 }} />
                )}
                {templateLoading && (
                  <CircularProgress size={20} sx={{ mt: 1, ml: 2 }} />
                )}
              </Grid>
            )}
            {docTypes?.length > 0 && (
              <Grid item xs={12}>
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { m: 0 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("email.availableDocuments", "Available Documents")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedAttachmentCount > 0
                          ? t("email.documentsChosen", { count: selectedAttachmentCount, defaultValue: "{{count}} document(s) chosen" })
                          : t("email.noDocumentsChosen", "No documents chosen")}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {otherAttachmentType && selectedOtherDocs.length === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={openOtherDocumentsSelector}
                        >
                          {t("email.chooseOtherDocuments", "Choose Other Documents")}
                        </Button>
                      </Box>
                    )}

                    {availableDocs.some((doc) => !isOtherType(doc.type)) && (
                      <List disablePadding>
                        {availableDocs
                          .filter((doc) => !isOtherType(doc.type))
                          .map((doc) => (
                            <React.Fragment key={`${doc.type}-${doc.fileId}`}>
                              <ListItem>
                                {doc.type === "VehicleInsurance" ? (
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedInsuranceDocs.includes(
                                          doc.directory,
                                        )}
                                        onChange={(event) =>
                                          handleInsuranceDocChange(
                                            doc.directory,
                                            event.target.checked,
                                          )
                                        }
                                        color="primary"
                                      />
                                    }
                                    label={
                                      <ListItemText
                                        primary={doc.fileName}
                                        secondary={`${t("common.labels.type", "Type")}: ${t(`settings.documentType.${doc.viewName.replace(/\s+/g, "")}`, doc.viewName)}`}
                                      />
                                    }
                                  />
                                ) : (
                                  <ListItemText
                                    primary={doc.fileName}
                                    secondary={`${t("common.labels.type", "Type")}: ${t(`settings.documentType.${doc.viewName.replace(/\s+/g, "")}`, doc.viewName)}`}
                                  />
                                )}
                                <ListItemSecondaryAction>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handlePreview(doc)}
                                    disabled={
                                      !docTypes?.find(
                                        (item: any) => item.type === doc.type,
                                      )?.previewFetch
                                    }
                                  >
                                    {t("common.buttons.preview", "Preview")}
                                  </Button>
                                </ListItemSecondaryAction>
                              </ListItem>
                              <Divider />
                            </React.Fragment>
                          ))}
                      </List>
                    )}

                    {selectedOtherDocs.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ px: 2, py: 1 }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            {t("email.chosenOtherDocuments", "Chosen Other Documents")}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={openOtherDocumentsSelector}
                          >
                            {t("email.adjustOtherDocuments", { count: selectedOtherDocs.length, defaultValue: `Adjust Other Documents (${selectedOtherDocs.length})` })}
                          </Button>
                        </Stack>
                        <List disablePadding>
                          {availableDocs
                            .filter(
                              (doc) =>
                                isOtherType(doc.type) &&
                                selectedOtherDocs.includes(getDocumentKey(doc)),
                            )
                            .map((doc) => (
                              <ListItem key={`chosen-${doc.fileId}`} divider>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked
                                      onChange={() =>
                                        removeSelectedOtherDocument(
                                          getDocumentKey(doc),
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <ListItemText
                                      primary={doc.fileName}
                                      secondary={
                                        getDocumentContentType(doc) ===
                                        "application/pdf"
                                          ? "PDF"
                                          : getDocumentContentType(
                                                doc,
                                              ).startsWith("image/")
                                            ? "Image"
                                            : "File"
                                      }
                                    />
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleOtherPreview(doc)}
                                    disabled={
                                      !otherDocType?.fileFetch ||
                                      !canPreviewOtherDocument(doc)
                                    }
                                  >
                                    {t("common.buttons.preview", "Preview")}
                                  </Button>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            {(availableDocs.some((doc) => !isOtherType(doc.type)) ||
              selectedOtherDocs.length > 0) && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <strong>{t("common.labels.attachedDocs")}</strong>{" "}
                  {availableDocs
                    .filter(
                      (doc) =>
                        (!isOtherType(doc.type) &&
                          doc.type !== "VehicleInsurance") ||
                        (doc.type === "VehicleInsurance" &&
                          selectedInsuranceDocs.includes(doc.directory)) ||
                        selectedOtherDocs.includes(getDocumentKey(doc)),
                    )
                    .map((doc) => doc.fileName)
                    .join(", ")}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: "#f5f5f5" }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={loading}
          sx={{ mr: 1 }}
        >
          {t("common.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {t("common.buttons.confirm")}
        </Button>
      </DialogActions>

      <Dialog
        open={vehicleInsuranceDocumentsOpen}
        onClose={cancelVehicleInsuranceDocumentSelection}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Vehicle Insurance Documents</DialogTitle>
        <DialogContent dividers>
          {docLoading ? (
            <Grid container justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Grid>
          ) : availableDocs.filter(
              (doc) =>
                doc.type === "VehicleInsurance",
            ).length > 0 ? (
            <List disablePadding>
              {availableDocs
                .filter(
                  (doc) =>
                    doc.type === "VehicleInsurance",
                )
                .map((doc) => (
                  <ListItem key={`${doc.type}-${doc.directory}`} divider>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draftSelectedInsuranceDocs.includes(
                            doc.directory,
                          )}
                          onChange={(event) =>
                            handleInsuranceDocChange(
                              doc.directory,
                              event.target.checked,
                            )
                          }
                          color="primary"
                        />
                      }
                      label={
                        <ListItemText
                          primary={doc.fileName}
                          secondary={`${t("common.labels.type", "Type")}: ${t(`settings.documentType.${doc.viewName.replace(/\s+/g, "")}`, doc.viewName)}`}
                        />
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handlePreview(doc)}
                        disabled={
                          !docTypes?.find(
                            (item: any) => item.type === doc.type,
                          )?.previewFetch
                        }
                      >
                        {t("common.buttons.preview", "Preview")}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          ) : (
            <Typography color="text.secondary" py={3} textAlign="center">
              No vehicle insurance documents found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {availableDocs.some(
            (doc) =>
              doc.type === "VehicleInsurance",
          ) ? (
            <>
              <Button onClick={cancelVehicleInsuranceDocumentSelection}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={confirmVehicleInsuranceDocumentSelection}
                disabled={draftSelectedInsuranceDocs.length === 0}
              >
                Add Selected ({draftSelectedInsuranceDocs.length})
              </Button>
            </>
          ) : (
            <Button onClick={cancelVehicleInsuranceDocumentSelection}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={otherDocumentsOpen}
        onClose={cancelOtherDocumentSelection}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("common.labels.selectOtherDocs")}</DialogTitle>
        <DialogContent dividers>
          {docLoading ? (
            <Grid container justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Grid>
          ) : availableDocs.filter((doc) => isOtherType(doc.type)).length >
            0 ? (
            <List disablePadding>
              {availableDocs
                .filter((doc) => isOtherType(doc.type))
                .map((doc) => (
                  <ListItem key={doc.fileId} divider>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draftSelectedOtherDocs.includes(
                            getDocumentKey(doc),
                          )}
                          onChange={(event) =>
                            handleOtherDocChange(
                              getDocumentKey(doc),
                              event.target.checked,
                            )
                          }
                          color="primary"
                        />
                      }
                      label={
                        <ListItemText
                          primary={doc.fileName}
                          secondary={
                            getDocumentContentType(doc) === "application/pdf"
                              ? t("common.labels.pdf", "PDF")
                              : getDocumentContentType(doc).startsWith("image/")
                                ? t("common.labels.image", "Image")
                                : getDocumentContentType(doc).startsWith(
                                      "text/",
                                    )
                                  ? t("common.labels.textDocument", "Text document")
                                  : t("common.labels.file", "File")
                          }
                        />
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOtherPreview(doc)}
                        disabled={
                          !otherDocType?.fileFetch ||
                          !canPreviewOtherDocument(doc)
                        }
                      >
                        {t("common.buttons.preview", "Preview")}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          ) : (
            <Typography color="text.secondary" py={3} textAlign="center">
              {t("common.messages.noDocumentsFound", "No documents found.")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {availableDocs.some((doc) => isOtherType(doc.type)) ? (
            <>
              <Button onClick={cancelOtherDocumentSelection}>{t("common.buttons.cancel")}</Button>
              <Button
                variant="contained"
                onClick={confirmOtherDocumentSelection}
                disabled={draftSelectedOtherDocs.length === 0}
              >
                {t("common.buttons.addSelected", "Add Selected")} ({draftSelectedOtherDocs.length})
              </Button>
            </>
          ) : (
            <Button onClick={cancelOtherDocumentSelection}>{t("common.buttons.close")}</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={otherPreviewOpen}
        onClose={closeOtherPreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {otherPreviewDoc?.fileName || t("common.labels.documentPreview", "Document Preview")}
        </DialogTitle>
        <DialogContent dividers sx={{ height: "70vh" }}>
          {otherPreviewDoc && otherPreviewUrl && (
            <>
              {getDocumentContentType(otherPreviewDoc) === "application/pdf" ||
              getDocumentContentType(otherPreviewDoc).startsWith("text/") ? (
                <iframe
                  src={otherPreviewUrl}
                  title={otherPreviewDoc.fileName}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              ) : (
                <Box
                  component="img"
                  src={otherPreviewUrl}
                  alt={otherPreviewDoc.fileName}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOtherPreview}>{t("common.buttons.close")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateDialogOpen}
        onClose={closeTemplateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("common.labels.selectTemplate")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("common.messages.selectPdfTemplateMsg", "Select the PDF template to attach to this email.")}
          </Typography>
          <List disablePadding>
            {templateOptions.map((template, index) => {
              const path = template.filePath || template.path;
              return (
                <React.Fragment
                  key={template.templateId ?? path ?? index}
                >
                  <ListItemButton onClick={() => selectPdfTemplate(template)}>
                    <ListItemText
                      primary={
                        template.templateName || template.name || t("common.labels.template", "Template")
                      }
                    />
                  </ListItemButton>
                  {index < templateOptions.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTemplateDialog}>{t("common.buttons.cancel")}</Button>
        </DialogActions>
      </Dialog>
      </Dialog>
    </FormProvider>
  );
};

export default EmailDialog;
